import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { downloadReferralTaxReportCsv } from '../../../../api/referralsApi';
import STRING_KEYS from '../../../../language/keys';
import { getTaxReportYears, saveBlobAsFile } from '../../referrals.utils';
import style from '../style';

type TaxReportDialogProps = {
  isOpen: boolean,
  onClose: () => void,
};

export const TaxReportDialog = ({ isOpen, onClose }: TaxReportDialogProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const years = getTaxReportYears(new Date());
  const [year, setYear] = useState<number>(years[0]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    setHasError(false);
    try {
      const csv = await downloadReferralTaxReportCsv(year);
      saveBlobAsFile(csv, `referral-tax-report-${year}.csv`);
      onClose();
    } catch (e) {
      setHasError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={ isOpen } onClose={ onClose } fullWidth maxWidth="xs">
      <DialogTitle>{ translate(STRING_KEYS.REFERRALS.TAX_REPORT_DIALOG_TITLE) }</DialogTitle>
      <DialogContent>
        <DialogContentText>{ translate(STRING_KEYS.REFERRALS.TAX_REPORT_DIALOG_SUB_TITLE) }</DialogContentText>
        <TextField
          select
          fullWidth
          label={ translate(STRING_KEYS.REFERRALS.TAX_REPORT_DIALOG_YEAR) }
          value={ year }
          onChange={ (e) => setYear(Number(e.target.value)) }
          sx={ style.field.sx }
        >
          { years.map((option) => (
            <MenuItem key={ option } value={ option }>{ option }</MenuItem>
          )) }
        </TextField>
        { hasError && (
          <Typography color="error" variant="body2" sx={ style.field.sx }>
            { translate(STRING_KEYS.REFERRALS.TAX_REPORT_ERROR) }
          </Typography>
        ) }
      </DialogContent>
      <DialogActions>
        <Button onClick={ onClose }>{ translate(STRING_KEYS.DIALOG.BTN_GO_BACK) }</Button>
        <Button variant="contained" disabled={ isDownloading } onClick={ handleDownload }>
          { translate(STRING_KEYS.REFERRALS.TAX_REPORT_DIALOG_BTN) }
        </Button>
      </DialogActions>
    </Dialog>
  );
};
